/**
 * FTR4: core Three.js scene, renderer, and Sol lighting + bloom.
 * Per creative brief §1: ONE directional light from Sol, low cool ambient,
 * bloom on Sol/nav only, pure-black space, logarithmicDepthBuffer ON.
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { createPostFx } from './postFx.js';

export interface SceneBundle {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  /** All world bodies live under this group; floating origin translates it. */
  worldGroup: THREE.Group;
  /** Sun directional light — repositioned each frame to point from Sol. */
  sunLight: THREE.DirectionalLight;
  composer: EffectComposer;
  bloom: UnrealBloomPass;
  /** AAA-19: fused vignette/grain/aberration pass — main.ts animates uTime. */
  postFx: ShaderPass;
  camera: THREE.PerspectiveCamera;
  setRenderScale: (scale: number) => void;
  resize: () => void;
}

/**
 * Subtle image-based lighting for space PBR. With no environment the sun-side
 * gets one flat highlight and the shadow side crushes to black, so hull/material
 * detail vanishes (the "not photoreal" read). A dim cool gradient dome — deep
 * space up top, near-black below, a soft sunward glow — fed through PMREM gives
 * every MeshStandard surface gentle ambient irradiance + soft specular. Kept
 * deliberately low (paired with a low scene.environmentIntensity) so it reads as
 * matte painted metal in starlight, NOT chrome — respecting AAA-21 (no glow).
 */
const buildSpaceEnvironment = (renderer: THREE.WebGLRenderer): THREE.Texture => {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(50, 32, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      top: { value: new THREE.Color(0x223240) },
      bottom: { value: new THREE.Color(0x05080c) },
      sunCol: { value: new THREE.Color(0x9aa6b4) },
      sunDir: { value: new THREE.Vector3(0, 0.18, 1).normalize() },
    },
    vertexShader:
      'varying vec3 vDir; void main(){ vDir = normalize(position);' +
      ' gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader:
      'varying vec3 vDir; uniform vec3 top; uniform vec3 bottom; uniform vec3 sunCol; uniform vec3 sunDir;' +
      ' void main(){ float h = clamp(vDir.y * 0.5 + 0.5, 0.0, 1.0);' +
      ' vec3 base = mix(bottom, top, h);' +
      ' float s = max(dot(normalize(vDir), normalize(sunDir)), 0.0);' +
      ' base += sunCol * pow(s, 5.0) * 0.5;' +
      ' gl_FragColor = vec4(base, 1.0); }',
  });
  envScene.add(new THREE.Mesh(geo, mat));
  const tex = pmrem.fromScene(envScene, 0.04).texture;
  geo.dispose();
  mat.dispose();
  pmrem.dispose();
  return tex;
};

/**
 * WAVE 2a — REAL IBL. Replaces the {@link buildSpaceEnvironment} 3-colour gradient
 * placeholder with a PMREM prefilter of the ACTUAL shipped Milky Way skybox EXR
 * (the same asset skybox.ts loads as `scene.background` — no new asset), with the
 * calibration-baseline sunward glow baked back in. Called from skybox.ts once the
 * EXR resolves, so the gradient dome is only the pre-load fallback.
 *
 * `environmentIntensity` (0.35 global) and the ship `envMapIntensity` 2.0 pattern
 * are UNCHANGED — this swaps the source of the irradiance, not the tuned exposure,
 * so hulls keep reading matte (the sky is dim; the glow is the only bright term).
 */
export const buildSkyboxEnvironment = (
  renderer: THREE.WebGLRenderer,
  skyTex: THREE.Texture,
  sunDir: THREE.Vector3 = new THREE.Vector3(0, 0.18, 1),
): THREE.Texture => {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  const geo = new THREE.SphereGeometry(50, 32, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      skyTex: { value: skyTex },
      skyIntensity: { value: 0.6 }, // matches skybox.ts backgroundIntensity
      sunCol: { value: new THREE.Color(0x9aa6b4) },
      sunDir: { value: sunDir.clone().normalize() },
    },
    vertexShader:
      'varying vec3 vDir; void main(){ vDir = normalize(position);' +
      ' gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader:
      'varying vec3 vDir; uniform sampler2D skyTex; uniform float skyIntensity;' +
      ' uniform vec3 sunCol; uniform vec3 sunDir;' +
      ' void main(){ vec3 d = normalize(vDir);' +
      // three's equirectangular convention (RECIPROCAL_PI2 / RECIPROCAL_PI).
      ' vec2 uv = vec2(atan(d.z, d.x) * 0.15915494 + 0.5, asin(clamp(d.y, -1.0, 1.0)) * 0.31830989 + 0.5);' +
      ' vec3 base = texture2D(skyTex, uv).rgb * skyIntensity;' +
      ' float s = max(dot(d, normalize(sunDir)), 0.0);' +
      ' base += sunCol * pow(s, 5.0) * 0.5;' +
      ' gl_FragColor = vec4(base, 1.0); }',
  });
  envScene.add(new THREE.Mesh(geo, mat));
  const tex = pmrem.fromScene(envScene, 0.04).texture;
  geo.dispose();
  mat.dispose();
  pmrem.dispose();
  return tex;
};

/**
 * WAVE 2a — SHADOW MAPS (gameplay only). Floating origin keeps the player bubble
 * at (0,0,0), so a single tight sun-aligned orthographic shadow camera parked a
 * fixed distance up-sun (see {@link SUN_SHADOW_LIGHT_DISTANCE_KM}) covers the
 * ~±20 km bubble where ships/stations/derelicts live — they self-shadow and cast
 * onto each other while bodies (thousands of km away) fall outside the frustum.
 * bias/normalBias are tuned for the ~20 m/texel bubble map to kill acne without
 * visible peter-panning; revisit if the box or map size changes.
 */
export const SUN_SHADOW_LIGHT_DISTANCE_KM = 50;
const SHADOW_ORTHO_HALF_KM = 20;
const SHADOW_MAP_SIZE = 2048;
const configureSunShadow = (renderer: THREE.WebGLRenderer, sun: THREE.DirectionalLight): void => {
  renderer.shadowMap.enabled = true;
  // three r183 deprecated PCFSoftShadowMap (it aliased to PCF with a console
  // warning) — PCFShadowMap is the same filtered lookup without the noise.
  renderer.shadowMap.type = THREE.PCFShadowMap;
  sun.castShadow = true;
  sun.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  const cam = sun.shadow.camera;
  cam.left = -SHADOW_ORTHO_HALF_KM;
  cam.right = SHADOW_ORTHO_HALF_KM;
  cam.top = SHADOW_ORTHO_HALF_KM;
  cam.bottom = -SHADOW_ORTHO_HALF_KM;
  cam.near = SUN_SHADOW_LIGHT_DISTANCE_KM - SHADOW_ORTHO_HALF_KM - 5;
  cam.far = SUN_SHADOW_LIGHT_DISTANCE_KM + SHADOW_ORTHO_HALF_KM + 5;
  cam.updateProjectionMatrix();
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.04;
};

/**
 * Multisampled composer render target. The default EffectComposer RT is created
 * with `samples: 0`, so the renderer's `antialias: true` MSAA is silently thrown
 * away the moment the frame is drawn through a composer — every world edge
 * aliases. A `samples: 4` HalfFloat target restores 4× MSAA for the whole post
 * chain while keeping the HDR headroom bloom needs (matching three's own default
 * HalfFloatType composer target, so no post-fx grade regresses).
 */
const createMsaaTarget = (renderer: THREE.WebGLRenderer): THREE.WebGLRenderTarget => {
  const size = renderer.getDrawingBufferSize(new THREE.Vector2());
  return new THREE.WebGLRenderTarget(size.width, size.height, {
    type: THREE.HalfFloatType,
    samples: 4,
  });
};

export const createScene = (canvas: HTMLCanvasElement): SceneBundle => {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    logarithmicDepthBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // WAVE 2a — TONEMAP: AgX (three r183) replaces ACES on the GAMEPLAY path. AgX
  // holds hue on the cyan accent (0x8ecfdb family) where ACES skewed it and gives
  // finer shadow gradation for the matte mourning palette. AgX reads darker than
  // ACES, so exposure is nudged 0.8 -> 1.0 (DELTA: +0.2) to hold the tuned planet
  // read; verify on the live deploy and retune minimally if bodies read dim. Bloom
  // is UNAFFECTED — it samples the linear RenderPass buffer before tonemapping, so
  // Sol's HDR emissive (>1, see bodies.makeSolEmissive) still crosses threshold 1.0
  // and NOTHING else does. The intro cinematic keeps ACES (introScene.ts, untouched).
  renderer.toneMapping = THREE.AgXToneMapping;
  renderer.toneMappingExposure = 1.0;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);
  // Image-based fill so PBR hulls read as lit material, not flat silhouettes.
  // environmentIntensity kept low: a starlight sheen, never a chrome reflection.
  scene.environment = buildSpaceEnvironment(renderer);
  // Gentle global fill (planets keep their tuned look); ship loaders boost their
  // own envMapIntensity for a stronger material read without over-lighting bodies.
  scene.environmentIntensity = 0.35;

  const worldGroup = new THREE.Group();
  scene.add(worldGroup);

  // near 0.01 (cockpit metres) .. far 1e12 (Mkm); log depth buffer makes this safe.
  const camera = new THREE.PerspectiveCamera(60, 1, 0.01, 1e12);

  const ambient = new THREE.AmbientLight(0x223344, 0.03);
  scene.add(ambient);

  // M1.1 BUG-2: drop intensity 3 -> 1.4. At intensity 3 under ACES the fully-lit
  // planet hemisphere clipped to white, then crossed the bloom threshold and
  // glowed — hiding the GLB textures. 1.4 keeps lit surfaces in tonemap range so
  // continents/bands/baseColor textures read clearly.
  const sunLight = new THREE.DirectionalLight(0xffffff, 1.4);
  sunLight.position.set(0, 0, 1);
  scene.add(sunLight);
  scene.add(sunLight.target);
  // WAVE 2a — SHADOW MAPS: enrol the one Sol key light as the shadow caster. The
  // light is repositioned each frame to a fixed distance up-sun (main.updateSunLight)
  // so this tight ortho frustum always straddles the player bubble.
  configureSunShadow(renderer, sunLight);

  const composer = new EffectComposer(renderer, createMsaaTarget(renderer));
  composer.addPass(new RenderPass(scene, camera));
  // M1.1 BUG-2: threshold raised to 1.0 so ONLY Sol (whose emissive material is
  // pushed into HDR > 1, see bodies.ts makeSolEmissive) crosses the bloom cutoff.
  // Lit planet surfaces tonemap below 1.0 at sunLight intensity 1.4 / exposure
  // 0.8, so they no longer bloom into white. (strength, radius, threshold)
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.6, 0.4, 1.0);
  composer.addPass(bloom);
  // AAA-19: cinematic grade (vignette + grain + aberration) then the MANDATORY
  // OutputPass — it owns final sRGB encoding now that a raw pass follows bloom,
  // preventing the gamma shift that would re-open BUG-2.
  const postFx = createPostFx();
  composer.addPass(postFx);
  composer.addPass(new OutputPass());

  let renderScale = 1;

  const setScaledSize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const ratio = Math.min(window.devicePixelRatio * renderScale, 2);
    renderer.setPixelRatio(ratio);
    renderer.setSize(w, h, false);
    composer.setPixelRatio(ratio);
    composer.setSize(w, h);
    const res = postFx.uniforms.uResolution;
    if (res) (res.value as THREE.Vector2).set(w * ratio, h * ratio);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  const setRenderScale = (scale: number): void => {
    renderScale = Math.max(0.5, Math.min(1.5, scale));
    setScaledSize();
  };

  const resize = (): void => {
    setScaledSize();
  };
  window.addEventListener('resize', resize);
  resize();

  return {
    renderer,
    scene,
    worldGroup,
    sunLight,
    composer,
    bloom,
    postFx,
    camera,
    setRenderScale,
    resize,
  };
};

// ===========================================================================
// IN-ENGINE CINEMATIC RENDER PATH (Black Marble intro).
// ---------------------------------------------------------------------------
// A dedicated, bloom-free grade used ONLY by the intro's own renderer/scene (it
// runs before the gameplay scene exists). The gameplay createScene() above is
// untouched — it keeps STONE_METALNESS lighting, exposure 0.8, and its bloom pass.
// ===========================================================================

/** Cold cyan-white key colour for the cinematic sun — rgb(207,230,238). */
export const CINEMATIC_SUN_COLOR = new THREE.Color(207 / 255, 230 / 255, 238 / 255);
/** Cinematic sun intensity — hotter than gameplay 1.4 because matte obsidian eats light. */
export const CINEMATIC_SUN_INTENSITY = 2.4;
/**
 * Cool ambient fill. Raised from the first cut's near-zero 0x0e1a1f @ 0.022 (which left
 * the shadowed hemisphere pure black and the monument unreadable) to a soft teal lift so
 * the dark side still reads as carved stone, matching the reference's legible terminator.
 */
export const CINEMATIC_AMBIENT_COLOR = 0x223540;
// Lower fill so the sun-side reads bright and the far hemisphere falls to a true dark
// terminator (the reference sphere is clearly lit-on-one-side, not a flat grey ball).
export const CINEMATIC_AMBIENT_INTENSITY = 0.11;

/** The cinematic composer bundle — RenderPass → Bokeh DOF → cinematic postFx → OutputPass (NO bloom, ever). */
export interface CinematicComposer {
  composer: EffectComposer;
  /**
   * DOF pass, in the STANDARD position (after RenderPass). The session-19 black-screen
   * bug came from using BokehPass as the FIRST/scene-rendering pass — as a middle pass
   * it reads the RenderPass colour buffer (tDiffuse) and only renders its own depth
   * pre-pass, which works reliably. Per-beat focus/aperture/maxblur are driven each
   * frame by IntroScene.applyDof off the CineClock.
   */
  bokeh: BokehPass | null;
  /** Cinematic vignette/grain/aberration pass — grain advances off the CineClock. */
  postFx: ShaderPass;
}

/**
 * Build the cinematic composer for the intro. `UnrealBloomPass` is DROPPED ENTIRELY (the
 * cinematic never blooms — the cold rim crescent is a Fresnel term in the material). The
 * scene is drawn by a standard `RenderPass`; BokehPass sits AFTER it as a pure DOF stage.
 */
export const createCinematicComposer = (
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
): CinematicComposer => {
  const composer = new EffectComposer(renderer, createMsaaTarget(renderer));
  composer.addPass(new RenderPass(scene, camera));
  // Neutral start values — IntroScene.applyDof retunes every frame per beat.
  const bokeh = new BokehPass(scene, camera, { focus: 120, aperture: 0.00003, maxblur: 0.005 });
  composer.addPass(bokeh);
  const postFx = createPostFx({ cinematic: true });
  composer.addPass(postFx);
  composer.addPass(new OutputPass()); // MANDATORY terminal sRGB encode
  return { composer, bokeh, postFx };
};
